import {
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { t } from '@hefe/i18n';
import {
  BasketballEvent,
  BasketballEventGame,
  BasketballEventPractice,
  BasketballEventStatus,
  BasketballEventType,
  BasketballEventTypeKey,
} from '@hefe/models';
import {
  BasketballGameActions,
  BasketballPracticeSaveRequest,
  BasketballPracticesSlice,
  BasketballTeamSlice,
  BasketballTeamsSlice,
  GymsSlice,
  ModalsSlice,
  usePrevious,
  userActiveBasketballCoachTeamId,
} from '@hefe/store';
import { formatDate, formatDateTime } from '@hefe/utils';
import { Button, Dropdown, Popconfirm, Spin, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomTable from '../../../core/components/table/custom-table.component';
import AddEditEventForm from '../../../events/forms/add-edit-event/add-edit-event.form';
import AddEditPracticeForm from '../../forms/add-edit-practice/add-edit-practice.form';
import AddPracticesForm from '../../forms/add-practices/add-practices.form';
import AddEditTeamGameForm from '../../forms/add-team-game/add-edit-team-game.form';
import EditPracticesForm from '../../forms/edit-practices/edit-practices.form';
import LinkPracticesForm from '../../forms/link-practices/link-practices.form';

export default function TeamEventsPage() {
  const dispatch = useAppDispatch();

  const events = useSelector(
    BasketballTeamSlice.basketballTeamEventsSelector
  ).map((event) => {
    return {
      ...event,
      id: `${event.type}_${event.id}`,
    };
  });
  const activeCoachId = useSelector(userActiveBasketballCoachTeamId);
  const prevActiveCoachId = usePrevious(activeCoachId);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadingActiveTeam, setLoadingActiveTeam] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const activeBasketballTeam = useSelector(
    BasketballTeamsSlice.basketballTeamSelector(activeCoachId ?? 0)
  );

  useEffect(() => {
    fetchEvents();
    fetchGyms();
    fetchTeams();
    fetchActiveTeam();
  }, []);

  useEffect(() => {
    if (
      prevActiveCoachId !== undefined &&
      activeCoachId !== prevActiveCoachId
    ) {
      fetchEvents();
      fetchActiveTeam();
    }
  }, [activeCoachId, prevActiveCoachId]);

  const fetchEvents = () => {
    if (!activeCoachId) {
      return;
    }
    setLoading(true);
    dispatch(BasketballTeamSlice.fetchBasketballTeamEvents(activeCoachId))
      .unwrap()
      .catch(() => null)
      .finally(() => setLoading(false));
  };

  const fetchGyms = () => {
    dispatch(GymsSlice.fetchGyms({ cacheDuration: 5 * 60 * 1000 }));
  };

  const fetchTeams = () => {
    dispatch(
      BasketballTeamsSlice.fetchBasketballTeamsSelect({
        cacheDuration: 5 * 60 * 1000,
      })
    );
  };

  const fetchActiveTeam = () => {
    if (!activeCoachId) {
      return;
    }
    setLoadingActiveTeam(true);
    dispatch(BasketballTeamsSlice.fetchBasketballTeam(activeCoachId))
      .unwrap()
      .finally(() => setLoadingActiveTeam(false));
  };

  const openAddGameForm = (game?: BasketballEventGame) => {
    const gameId = game ? game.id : undefined;
    dispatch(
      ModalsSlice.modalsActions.openModal({
        component: <AddEditTeamGameForm gameId={gameId} />,
        title: t('games.add'),
        formId: `add-edit-team-game-form-${gameId || 'new'}`,
      })
    );
  };

  const openAddSinglePracticeForm = (practice?: BasketballEventPractice) => {
    let formData: BasketballPracticeSaveRequest | undefined = undefined;
    let practiceId = undefined;

    if (practice) {
      practiceId = practice.id;
      const startDate = dayjs(practice.date);
      const endDate = dayjs(practice.end);

      formData = {
        ...practice,
        id: practiceId,
        date: practice.date,
        duration: endDate.diff(startDate, 'minutes'),
        team_ids: practice.teams.map((team) => team.id),
        canceled: practice.status === BasketballEventStatus.CANCELED,
      };
    }

    dispatch(
      ModalsSlice.modalsActions.openModal({
        component: <AddEditPracticeForm practice={formData} />,
        title: t('practices.add'),
        formId: `add-edit-practice-form-${practiceId || 'new'}`,
      })
    );
  };

  const openAddEventForm = (event?: BasketballEvent) => {
    const eventId = event ? event.id : undefined;
    dispatch(
      ModalsSlice.modalsActions.openModal({
        component: (
          <AddEditEventForm
            eventId={eventId}
            teamId={activeCoachId ?? undefined}
          />
        ),
        title: t('events.events.add'),
        formId: `add-edit-event-form-${eventId ?? 'new'}`,
      })
    );
  };

  const openAddMultiPracticeForm = (practices?: BasketballEventPractice[]) => {
    if (practices) {
      const practiceIds = practices.map((practice) => practice.id);

      dispatch(
        ModalsSlice.modalsActions.openModal({
          component: (
            <EditPracticesForm
              practice={practices[0]}
              practiceIds={practiceIds}
            />
          ),
          title: t('practices.edit_plural'),
          formId: `edit-practices-form`,
        })
      );
    } else {
      dispatch(
        ModalsSlice.modalsActions.openModal({
          component: <AddPracticesForm />,
          title: t('practices.add'),
          formId: `add-practices-form`,
        })
      );
    }
  };

  const openLinkPracticesForm = () => {
    dispatch(
      ModalsSlice.modalsActions.openModal({
        component: <LinkPracticesForm />,
        title: t('practices.link'),
        formId: `link-practices-form`,
      })
    );
  };

  const openEvent = (event: BasketballEvent) => {
    const eventId = parseInt((event.id + '').substring(2));
    if (!eventId) {
      return;
    }

    if (event.type === BasketballEventType.GAME) {
      dispatch(
        ModalsSlice.modalsActions.openGameModal({
          id: eventId,
        })
      );
    } else if (event.type === BasketballEventType.PRACTICE) {
      dispatch(
        ModalsSlice.modalsActions.openPracticeModal({
          id: eventId,
        })
      );
    } else if (event.type === BasketballEventType.EVENT) {
      dispatch(
        ModalsSlice.modalsActions.openEventModal({
          id: eventId,
        })
      );
    }
  };

  const editEvents = () => {
    if (selectedRowKeys.length === 1) {
      const event = events.find((e) => e.id === selectedRowKeys[0]);
      if (event) {
        const editEvent = {
          ...event,
          id: parseInt(event.id.substring(2)),
        };

        if (event.type === BasketballEventType.GAME) {
          openAddGameForm(editEvent as BasketballEventGame);
        } else if (event.type === BasketballEventType.PRACTICE) {
          openAddSinglePracticeForm(editEvent as BasketballEventPractice);
        } else if (event.type === BasketballEventType.EVENT) {
          openAddEventForm(editEvent);
        }
      }
    } else {
      const practices = events
        .filter((e) => selectedRowKeys.includes(e.id))
        .map((e) => {
          return {
            ...e,
            id: parseInt(e.id.substring(2)),
          };
        });
      if (practices) {
        openAddMultiPracticeForm(practices as BasketballEventPractice[]);
      }
    }
  };

  const deleteEvents = () => {
    const selectedGames = selectedRowKeys.filter((key) =>
      key.startsWith(`${BasketballEventType.GAME}_`)
    );
    const selectedPractices = selectedRowKeys.filter((key) =>
      key.startsWith(`${BasketballEventType.PRACTICE}_`)
    );

    if (selectedGames.length === 1) {
      const event = events.find((e) => e.id === selectedGames[0]);
      if (event) {
        const eventId = parseInt(event.id.substring(2));
        if (event.type === BasketballEventType.GAME) {
          setDeleting(true);
          dispatch(BasketballGameActions.deleteBasketballGame(eventId))
            .unwrap()
            .catch(() => null)
            .finally(() => setDeleting(false));
        }
      }
    }

    if (selectedGames.length === 0 && selectedPractices.length > 0) {
      const practiceIds = selectedPractices.map((key) =>
        parseInt(key.substring(2))
      );
      setDeleting(true);
      dispatch(BasketballPracticesSlice.deleteBasketballPractices(practiceIds))
        .unwrap()
        .catch(() => null)
        .finally(() => setDeleting(false));
    }
  };

  const autoSyncText = useMemo(() => {
    if (loadingActiveTeam) {
      return <Spin size="small" />;
    }

    if (activeBasketballTeam?.sync_practices_until) {
      return t('practices.sync.status.until', {
        date: formatDate(activeBasketballTeam?.sync_practices_until),
      });
    }
    return t('practices.sync.status.no_sync');
  }, [activeBasketballTeam?.sync_practices_until, loadingActiveTeam]);

  const isEditBtnDisabled = (): boolean => {
    const selectedGames = selectedRowKeys.filter((key) =>
      key.startsWith(`${BasketballEventType.GAME}_`)
    );
    const selectedPractices = selectedRowKeys.filter((key) =>
      key.startsWith(`${BasketballEventType.PRACTICE}_`)
    );
    const selectedEvents = selectedRowKeys.filter((key) =>
      key.startsWith(`${BasketballEventType.EVENT}_`)
    );

    if (
      selectedGames.length > 0 &&
      selectedPractices.length > 0 &&
      selectedEvents.length > 0
    ) {
      return true;
    }

    // Allow practice multi edit
    if (selectedPractices.length > 0) {
      return false;
    }

    // Only allow to edit one game at a time
    if (selectedGames.length === 1) {
      return false;
    }

    if (selectedEvents.length === 1) {
      return false;
    }

    return true;
  };

  const isDeleteBtnDisabled = (): boolean => {
    const selectedGames = selectedRowKeys.filter((key) =>
      key.startsWith(`${BasketballEventType.GAME}_`)
    );
    const selectedPractices = selectedRowKeys.filter((key) =>
      key.startsWith(`${BasketballEventType.PRACTICE}_`)
    );
    const selectedEvents = selectedRowKeys.filter((key) =>
      key.startsWith(`${BasketballEventType.EVENT}_`)
    );

    if (selectedRowKeys.length === 0) {
      return true;
    }

    if (selectedGames.length > 1) {
      return true;
    }

    if (selectedEvents.length > 1) {
      return true;
    } else if (selectedEvents.length === 1) {
      const event = events.find((e) => e.id === selectedEvents[0]);
      return event?.editable ?? false;
    }

    if (
      selectedGames.length > 0 &&
      selectedPractices.length > 0 &&
      selectedEvents.length > 0
    ) {
      return true;
    }

    return false;
  };

  const deletePopconfirmText = (): string => {
    const selectedGames = selectedRowKeys.filter((key) =>
      key.startsWith(`${BasketballEventType.GAME}_`)
    );
    const selectedPractices = selectedRowKeys.filter((key) =>
      key.startsWith(`${BasketballEventType.PRACTICE}_`)
    );

    if (selectedGames.length > 0) {
      return 'questions.delete_game';
    }

    if (selectedPractices.length > 0) {
      return 'questions.delete_practices';
    }

    return '';
  };

  const disabledRowKeys = (): string[] => {
    return events
      .filter((event) => {
        if (event.type === BasketballEventType.PRACTICE) {
          return (
            (
              {
                ...event,
                id: parseInt(event.id.substring(2)),
              } as BasketballEventPractice
            ).editable === false
          );
        }
        if (event.type === BasketballEventType.GAME) {
          return (
            (
              {
                ...event,
                id: parseInt(event.id.substring(2)),
              } as BasketballEventGame
            ).league_id !== null
          );
        }
        return false;
      })
      .map((event) => event.id);
  };

  const strikeThroughRowKeys = (): string[] => {
    return events
      .filter((event) => event.status === BasketballEventStatus.CANCELED)
      .map((event) => event.id);
  };

  return (
    <>
      <Typography.Paragraph style={{ marginTop: 10, marginBottom: 10 }}>
        {autoSyncText}
      </Typography.Paragraph>
      <CustomTable
        name="team-events"
        rowKey="id"
        loading={loading}
        rowSelection
        onRowClick={(event: BasketballEvent) => openEvent(event)}
        onRowSelect={(keys) => setSelectedRowKeys(keys)}
        dataSource={events}
        disabledRowKeys={disabledRowKeys()}
        strikeThroughRowKeys={strikeThroughRowKeys()}
        columns={[
          {
            key: 'type',
            title: t('type'),
            dataIndex: 'type',
            showFilter: true,
            sortType: 'string',
            default: true,
            render: (value: number, event: BasketballEvent) => {
              const key: string = BasketballEventTypeKey[value];
              const returnValue = key ? t(key) : '';
              const generated =
                event.type === BasketballEventType.PRACTICE &&
                (event as BasketballEventPractice).generated;

              return (
                <Typography.Text>
                  {generated && (
                    <Tooltip title={t('practices.sync.icon.tooltip')}>
                      <LinkOutlined />
                      &nbsp;
                    </Tooltip>
                  )}
                  {returnValue}
                </Typography.Text>
              );
            },
          },
          {
            key: 'date',
            title: t('date'),
            dataIndex: 'date',
            showDateFilter: true,
            sortType: 'date',
            default: true,
            render: (value: string) =>
              value ? formatDateTime(value, { withWeekday: 'short' }) : '',
          },
          {
            key: 'description',
            title: t('title'),
            dataIndex: 'description',
            default: true,
          },
          {
            key: 'location',
            title: t('location'),
            dataIndex: 'location',
            default: true,
          },
          {
            key: 'limit',
            title: t('limit'),
            dataIndex: 'limit',
            default: false,
            render: (value: number) => (value ? value : '∞'),
          },
        ]}
        toolbarItems={[
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                {
                  key: 1,
                  label: t('games.singular'),
                  onClick: () => openAddGameForm(),
                },
                {
                  key: 2,
                  label: t('events.events.add'),
                  onClick: () => openAddEventForm(),
                },
                {
                  key: 3,
                  label: t('practices.singular'),
                  onClick: () => openAddSinglePracticeForm(),
                },
                {
                  key: 4,
                  label: t('practices.plural'),
                  onClick: () => openAddMultiPracticeForm(),
                },
                {
                  key: 5,
                  label: t('practices.link'),
                  onClick: () => openLinkPracticesForm(),
                },
              ],
            }}
          >
            <Button type="primary" icon={<PlusOutlined />}></Button>
          </Dropdown>,
          <Button
            icon={<EditOutlined />}
            disabled={isEditBtnDisabled()}
            onClick={() => editEvents()}
          />,
          <Popconfirm
            title={t(deletePopconfirmText())}
            disabled={deleting || isDeleteBtnDisabled()}
            onConfirm={() => deleteEvents()}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={isDeleteBtnDisabled()}
              loading={deleting}
            />
          </Popconfirm>,
        ]}
      />
    </>
  );
}
